<template>
    <div class="files-manager">
        <!-- Header estilo panel con título, buscador, filtros y botón cerrar -->
        <div class="panel-header">
            <div class="header-left">
                <h3><i class="fas fa-folder-open"></i> Gestión de Documentos</h3>
            </div>
            <div class="header-center">
                <SearchFilterClose
v-model="searchQuery" placeholder="Buscar archivos..." class="search-component"
                    @input="debouncedSearch" />
            </div>
            <div class="header-buttons">
                <argon-button color="success" variant="gradient" size="sm" @click="showUploader = true">
                    <i class="fas fa-cloud-upload-alt"></i>
                    Subir Archivos
                </argon-button>
                <button class="cerrar-btn" @click="closeManager">&times;</button>
            </div>
        </div>

        <!-- Filtros y controles en barra secundaria -->
        <div class="filters-bar">
            <div class="filter-group">
                <label>Tipo:</label>
                <select v-model="filters.fileType" class="form-select" @change="loadFiles">
                    <option value="">Todos</option>
                    <option value="laboratorio">Laboratorio</option>
                    <option value="estudio">Estudios</option>
                    <option value="imagen">Imágenes</option>
                    <option value="radiografia">Radiografías</option>
                    <option value="tomografia">Tomografías</option>
                    <option value="resonancia">Resonancias</option>
                    <option value="ultrasonido">Ultrasonidos</option>
                    <option value="electrocardiograma">Electrocardiogramas</option>
                    <option value="documento">Documentos</option>
                    <option value="receta">Recetas</option>
                    <option value="consentimiento">Consentimientos</option>
                    <option value="referencia">Referencias</option>
                    <option value="otro">Otros</option>
                </select>
            </div>
            <div class="filter-group">
                <label>Vista:</label>
                <div class="view-toggle">
                    <button
:class="{ active: viewMode === 'grid' }" title="Vista cuadrícula"
                        @click="viewMode = 'grid'">
                        <i class="fas fa-th"></i>
                    </button>
                    <button :class="{ active: viewMode === 'list' }" title="Vista lista" @click="viewMode = 'list'">
                        <i class="fas fa-list"></i>
                    </button>
                </div>
            </div>
            <div class="filter-group">
                <label>Ordenar:</label>
                <select v-model="filters.sortColumn" class="form-select" @change="loadFiles">
                    <option value="created_at">Fecha de creación</option>
                    <option value="name">Nombre</option>
                    <option value="file_size">Tamaño</option>
                </select>
                <button class="btn-sort" @click="toggleSortOrder">
                    <i :class="filters.sortOrder === 'asc' ? 'fas fa-sort-amount-up' : 'fas fa-sort-amount-down'"></i>
                </button>
            </div>
        </div>

        <!-- Estadísticas -->
        <div v-if="stats" class="stats-bar">
            <div class="stat-card">
                <i class="fas fa-file"></i>
                <div class="stat-info">
                    <span class="stat-value">{{ stats.total_files || 0 }}</span>
                    <span class="stat-label">Total de archivos</span>
                </div>
            </div>
            <div class="stat-card">
                <i class="fas fa-folder"></i>
                <div class="stat-info">
                    <span class="stat-value">{{ stats.general_files || 0 }}</span>
                    <span class="stat-label">Archivos generales</span>
                </div>
            </div>
            <div class="stat-card">
                <i class="fas fa-notes-medical"></i>
                <div class="stat-info">
                    <span class="stat-value">{{ stats.consultation_files || 0 }}</span>
                    <span class="stat-label">De consultas</span>
                </div>
            </div>
            <div class="stat-card">
                <i class="fas fa-database"></i>
                <div class="stat-info">
                    <span class="stat-value">{{ stats.total_size_human || '0 MB' }}</span>
                    <span class="stat-label">Espacio usado</span>
                </div>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-container">
            <div class="spinner"></div>
            <p>Cargando archivos...</p>
        </div>

        <!-- Files Grid/List -->
        <div v-else-if="files.length > 0" :class="['files-container', viewMode]">
            <div v-for="file in files" :key="file.id" class="file-card" @click="selectFile(file)">
                <div class="file-preview">
                    <i :class="getFileIcon(file)"></i>
                    <div v-if="file.is_favorite" class="favorite-badge" title="Favorito">
                        <i class="fas fa-star"></i>
                    </div>
                </div>
                <div class="file-details">
                    <h4 class="file-name" :title="file.name">{{ file.name }}</h4>
                    <div class="file-meta">
                        <span v-if="file.file_type" class="file-type-badge">
                            {{ formatFileType(file.file_type) }}
                        </span>
                        <span class="file-size">
                            <i class="fas fa-weight"></i>
                            {{ formatFileSize(file.file_size) }}
                        </span>
                        <span class="file-date">
                            <i class="far fa-calendar"></i>
                            {{ formatDate(file.created_at) }}
                        </span>
                    </div>
                    <p v-if="file.description" class="file-description">
                        {{ file.description }}
                    </p>
                    <div v-if="file.patient_medical_note_id" class="file-consultation">
                        <i class="fas fa-notes-medical"></i>
                        <span>Consulta #{{ file.patient_medical_note_id }}</span>
                    </div>
                </div>
                <div class="file-actions">
                    <button class="btn-action btn-download" title="Descargar archivo" @click.stop="downloadFile(file)">
                        <i class="fas fa-download"></i>
                        <span class="btn-text">Descargar</span>
                    </button>
                    <button class="btn-action btn-view" title="Vista previa" @click.stop="viewFile(file)">
                        <i class="fas fa-eye"></i>
                        <span class="btn-text">Ver</span>
                    </button>
                    <button class="btn-action btn-delete" title="Eliminar archivo" @click.stop="confirmDelete(file)">
                        <i class="fas fa-trash-alt"></i>
                        <span class="btn-text">Eliminar</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
            <div class="empty-state-card">
                <div class="empty-icon-container">
                    <i class="fas fa-folder-open"></i>
                </div>
                <h3>No hay archivos</h3>
                <p>Sube tu primer archivo para comenzar a gestionar los documentos médicos del paciente</p>
                <argon-button
color="primary" variant="gradient" size="lg" class="upload-button"
                    @click="showUploader = true">
                    <i class="fas fa-cloud-upload-alt"></i>
                    Subir Archivos
                </argon-button>
                <div class="empty-features">
                    <div class="feature-item">
                        <i class="fas fa-check-circle"></i>
                        <span>Soporta múltiples formatos</span>
                    </div>
                    <div class="feature-item">
                        <i class="fas fa-check-circle"></i>
                        <span>Hasta 10MB por archivo</span>
                    </div>
                    <div class="feature-item">
                        <i class="fas fa-check-circle"></i>
                        <span>Almacenamiento seguro en la nube</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.total > pagination.perPage" class="pagination-container">
            <button
class="btn-page" :disabled="pagination.currentPage === 1"
                @click="changePage(pagination.currentPage - 1)">
                <i class="fas fa-chevron-left"></i>
            </button>
            <span class="page-info">
                Página {{ pagination.currentPage }} de {{ pagination.lastPage }}
            </span>
            <button
class="btn-page" :disabled="pagination.currentPage === pagination.lastPage"
                @click="changePage(pagination.currentPage + 1)">
                <i class="fas fa-chevron-right"></i>
            </button>
        </div>

        <!-- Modal Uploader con BaseModal -->
        <BaseModal
v-model="showUploader" title="Subir Documentos" icon="fas fa-cloud-upload-alt" :show-footer="false"
            :no-padding="true" size="large">
            <FileUploader
ref="fileUploaderRef" :patient-id="patientId" :patient-medical-note-id="patientMedicalNoteId"
                :show-header="false" @uploaded="onFileUploaded" @complete="onUploadComplete" />
        </BaseModal>

        <!-- Modal File Viewer -->
        <teleport to="body">
            <div v-if="selectedFile" class="modal-overlay" @click.self="selectedFile = null">
                <div class="modal-content modal-large">
                    <button class="modal-close" @click="selectedFile = null">
                        <i class="fas fa-times"></i>
                    </button>

                    <!-- Loading indicator -->
                    <div v-if="loadingFile" class="file-viewer loading-viewer">
                        <div class="loading-content">
                            <i class="fas fa-spinner fa-spin fa-3x"></i>
                            <p>Cargando vista previa segura...</p>
                        </div>
                    </div>

                    <!-- File viewer -->
                    <div v-else class="file-viewer">
                        <div class="viewer-header">
                            <h3>{{ selectedFile.name }}</h3>
                            <button
title="Copiar URL firmada al portapapeles (válida por 60 minutos)"
                                class="btn-copy-url" @click="copySignedUrl(selectedFile)">
                                <i class="fas fa-copy"></i>
                                Copiar URL Firmada
                            </button>
                            <button class="btn-primary" @click="downloadFile(selectedFile)">
                                <i class="fas fa-download"></i>
                                Descargar
                            </button>
                        </div>
                        <div class="viewer-content">
                            <iframe
v-if="canPreview(selectedFile)" :src="selectedFile.signed_url"
                                frameborder="0"></iframe>
                            <div v-else class="no-preview">
                                <i class="fas fa-file fa-4x"></i>
                                <p>Vista previa no disponible</p>
                                <button class="btn-primary" @click="downloadFile(selectedFile)">
                                    <i class="fas fa-download"></i>
                                    Descargar para ver
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </teleport>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, getCurrentInstance } from 'vue';
import api from '@/services/api';
import FileUploader from './FileUploader.vue';
import SearchFilterClose from '@/components/SearchFilterClose.vue';
import ArgonButton from '@/components/ArgonButton.vue';
import BaseModal from '@/components/BaseModal.vue';
import Swal from 'sweetalert2';

const { proxy } = getCurrentInstance();

const props = defineProps({
    patientId: {
        type: [Number, String],
        required: true
    },
    patientMedicalNoteId: {
        type: [Number, String],
        default: null
    }
});

// Refs
const files = ref([]);
const stats = ref(null);
const loading = ref(false);
const searchQuery = ref('');
const viewMode = ref('grid');
const showUploader = ref(false);
const selectedFile = ref(null);
const loadingFile = ref(false); // Para indicar carga de signed_url
const fileUploaderRef = ref(null);

const filters = ref({
    fileType: '',
    sortColumn: 'created_at',
    sortOrder: 'desc'
});

const pagination = ref({
    currentPage: 1,
    lastPage: 1,
    perPage: 12,
    total: 0
});

// Methods
const loadFiles = async () => {
    loading.value = true;
    try {
        const params = {
            patient_id: props.patientId,
            per_page: pagination.value.perPage,
            page: pagination.value.currentPage,
            sort_column: filters.value.sortColumn,
            sort_order: filters.value.sortOrder
        };

        if (props.patientMedicalNoteId) {
            params.patient_medical_note_id = props.patientMedicalNoteId;
        }

        if (filters.value.fileType) {
            params.file_type = filters.value.fileType;
        }

        if (searchQuery.value) {
            params.search = searchQuery.value;
        }

        const response = await api.get('/patient-files', { params });
        files.value = response.data.data;

        // Update pagination
        if (response.data.meta) {
            pagination.value = {
                currentPage: response.data.meta.current_page,
                lastPage: response.data.meta.last_page,
                perPage: response.data.meta.per_page,
                total: response.data.meta.total
            };
        }
    } catch (error) {
        console.error('Error loading files:', error);
    } finally {
        loading.value = false;
    }
};

const loadStats = async () => {
    try {
        const response = await api.get(`/patients/${props.patientId}/files/stats`);
        stats.value = response.data.data;
    } catch (error) {
        console.error('Error loading stats:', error);
    }
};

const downloadFile = async (file) => {
    try {
        const response = await api.get(`/patient-files/${file.id}/download`, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
    } catch (error) {
        console.error('Error downloading file:', error);
        alert('Error al descargar el archivo');
    }
};

const copySignedUrl = async (file) => {
    try {
        if (!file.signed_url) {
            alert('URL firmada no disponible');
            return;
        }

        // Copiar al portapapeles
        await navigator.clipboard.writeText(file.signed_url);

        // Notificación de éxito
        if (proxy.$notify) {
            proxy.$notify(
                'URL Copiada',
                'La URL firmada ha sido copiada al portapapeles. Válida por 60 minutos.',
                'success',
                'fas fa-check-circle'
            );
        } else {
            alert('URL firmada copiada al portapapeles\n\n⏰ Válida por 60 minutos');
        }
    } catch (error) {
        console.error('Error copying signed URL:', error);

        // Fallback: Mostrar la URL para copiar manualmente
        prompt(
            'No se pudo copiar automáticamente. Copia esta URL (válida por 60 minutos):',
            file.signed_url
        );
    }
};

const viewFile = async (file) => {
    loadingFile.value = true;
    try {
        // Obtener el archivo con signed_url desde el backend
        const response = await api.get(`/patient-files/${file.id}`);
        selectedFile.value = response.data.data; // Incluye signed_url
    } catch (error) {
        console.error('Error loading file details:', error);
        alert('Error al cargar el archivo');
        selectedFile.value = null;
    } finally {
        loadingFile.value = false;
    }
};

const selectFile = (file) => {
    viewFile(file);
};

const confirmDelete = async (file) => {
    const result = await Swal.fire({
        title: '¿Estás seguro?',
        html: `¡No podrás revertir esto!<br>¿Deseas eliminar "${file.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Sí, elimínalo',
        customClass: {
            confirmButton: 'btn bg-gradient-success',
            cancelButton: 'btn bg-gradient-danger'
        },
        buttonsStyling: false
    });

    if (!result.isConfirmed) {
        return;
    }

    try {
        await api.delete(`/patient-files/${file.id}`);
        await loadFiles();
        await loadStats();
        
        proxy.$notify(
            'Archivo eliminado correctamente',
            '',
            'success',
            'ni ni-check-bold ni-lg'
        );
    } catch (error) {
        console.error('Error deleting file:', error);
        proxy.$notify(
            error.response?.data?.message || 'Error al eliminar el archivo',
            '',
            'danger',
            'ni ni-fat-remove ni-lg'
        );
    }
};

const changePage = (page) => {
    pagination.value.currentPage = page;
    loadFiles();
};

const toggleSortOrder = () => {
    filters.value.sortOrder = filters.value.sortOrder === 'asc' ? 'desc' : 'asc';
    loadFiles();
};

let searchTimeout;
const debouncedSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        pagination.value.currentPage = 1;
        loadFiles();
    }, 500);
};

const onFileUploaded = () => {
    loadFiles();
    loadStats();
};

const onUploadComplete = (summary) => {
    showUploader.value = false;

    // Mostrar notificación de éxito
    if (summary && summary.success > 0) {
        const mensaje = summary.success === 1
            ? '1 archivo subido exitosamente'
            : `${summary.success} archivos subidos exitosamente`;

        proxy.$notify(
            mensaje,
            '',
            'success',
            'fas fa-cloud-upload-alt'
        );
    }
};

const getFileIcon = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    const iconMap = {
        pdf: 'fas fa-file-pdf text-danger',
        doc: 'fas fa-file-word text-primary',
        docx: 'fas fa-file-word text-primary',
        xls: 'fas fa-file-excel text-success',
        xlsx: 'fas fa-file-excel text-success',
        csv: 'fas fa-file-csv text-success',
        jpg: 'fas fa-file-image text-info',
        jpeg: 'fas fa-file-image text-info',
        png: 'fas fa-file-image text-info',
        gif: 'fas fa-file-image text-info'
    };
    return iconMap[ext] || 'fas fa-file text-secondary';
};

const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

const formatFileType = (type) => {
    const types = {
        laboratorio: 'Laboratorio',
        estudio: 'Estudio',
        imagen: 'Imagen',
        radiografia: 'Radiografía',
        tomografia: 'Tomografía',
        resonancia: 'Resonancia',
        ultrasonido: 'Ultrasonido',
        electrocardiograma: 'ECG',
        documento: 'Documento',
        receta: 'Receta',
        consentimiento: 'Consentimiento',
        referencia: 'Referencia',
        otro: 'Otro'
    };
    return types[type] || type;
};

const canPreview = (file) => {
    const previewable = ['pdf', 'jpg', 'jpeg', 'png', 'gif'];
    const ext = file.name.split('.').pop().toLowerCase();
    return previewable.includes(ext);
};

// Close manager
const closeManager = () => {
    // Cerrar el panel desde el padre
    window.dispatchEvent(new CustomEvent('close-files-manager'));
};

// Watch para recargar archivos cuando cambia el paciente
watch(() => props.patientId, (newPatientId, oldPatientId) => {
    if (newPatientId && newPatientId !== oldPatientId) {
        // Resetear estado
        pagination.value.currentPage = 1;
        searchQuery.value = '';
        selectedFile.value = null;

        // Recargar archivos del nuevo paciente
        loadFiles();
        loadStats();
    }
}, { immediate: false });

// Lifecycle
onMounted(() => {
    loadFiles();
    loadStats();
});
</script>

<style scoped>
/* Estilos modernos estilo panel DetailsPanel */

.files-manager {
    background: white;
    border-radius: 0;
    overflow: hidden;
    min-height: 600px;
    height: 100%;
}

/* Header estilo panel - igual que DetailsPanel */
.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    backdrop-filter: blur(20px);
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 2px 12px rgba(15, 23, 42, 0.08);
    transition: box-shadow 0.3s ease;
    gap: 2rem;
}

.panel-header::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent 0%, #1e40af 50%, transparent 100%);
    opacity: 0.2;
}

.panel-header:hover {
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
}

.header-left h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    white-space: nowrap;
}

.header-left h3 i {
    color: #1e40af;
    font-size: 1.5rem;
}

.header-center {
    flex: 1;
    display: flex;
    justify-content: center;
    max-width: 500px;
}

.search-component {
    width: 100%;
}

.header-buttons {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

/* Botón cerrar estilo panel */
.cerrar-btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.1);
    background: white;
    color: #64748b;
    font-size: 1.5rem;
    font-weight: 300;
    line-height: 1;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
}

.cerrar-btn:hover {
    background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
    color: #ffffff;
    border-color: transparent;
    transform: translateY(-2px) rotate(90deg);
    box-shadow: 0 4px 16px rgba(220, 38, 38, 0.35);
}

.filters-bar {
    display: flex;
    gap: 1rem;
    padding: 0.75rem 1.5rem;
    background: #f8f9fa;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    flex-wrap: wrap;
    align-items: center;
}

.filter-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.filter-group label {
    font-weight: 500;
    color: #6b7280;
    font-size: 0.875rem;
}

.form-select {
    padding: 0.5rem 0.75rem;
    border: 1px solid rgba(15, 23, 42, 0.15);
    border-radius: 6px;
    background: white;
    color: #475569;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;
}

.form-select:hover {
    border-color: #667eea;
}

.form-select:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.btn-sort {
    background: white;
    border: 1px solid rgba(15, 23, 42, 0.15);
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #475569;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
}

.btn-sort:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: transparent;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.btn-sort i {
    font-size: 1rem;
}

.view-toggle {
    display: flex;
    gap: 0.25rem;
}

.view-toggle button {
    background: white;
    border: 1px solid rgba(15, 23, 42, 0.15);
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 6px;
    color: #475569;
}

.view-toggle button:hover {
    border-color: #667eea;
    color: #667eea;
}

.view-toggle button.active {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: transparent;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.stats-bar {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    padding: 1rem 1.5rem;
    background: #f8f9fa;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.stat-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.875rem 1rem;
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
    transition: all 0.2s ease;
}

.stat-card:hover {
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
    transform: translateY(-2px);
}

.stat-card i {
    font-size: 1.75rem;
    color: #667eea;
}

.stat-info {
    display: flex;
    flex-direction: column;
}

.stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
}

.stat-label {
    font-size: 0.875rem;
    color: #6b7280;
}

.files-container {
    padding: 1rem 1.5rem;
    min-height: 400px;
}

.files-container.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 1rem;
}

.files-container.list .file-card {
    display: flex;
    flex-direction: row;
    padding: 1rem;
}

.file-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1.5rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.file-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
}

.file-card:hover {
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15);
    transform: translateY(-4px);
    border-color: #667eea;
}

.file-card:hover::before {
    transform: scaleX(1);
}

.file-preview {
    font-size: 3.5rem;
    text-align: center;
    margin-bottom: 1rem;
    color: #667eea;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
}

.favorite-badge {
    position: absolute;
    top: -0.5rem;
    right: -0.5rem;
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    box-shadow: 0 2px 8px rgba(251, 191, 36, 0.4);
}

.file-details {
    flex: 1;
}

.file-name {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #1f2937;
}

.file-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 0.75rem;
}

.file-meta span {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.file-meta i {
    font-size: 0.7rem;
    opacity: 0.7;
}

.file-type-badge {
    background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
    color: #7c3aed;
    padding: 0.25rem 0.65rem;
    border-radius: 6px;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.file-size {
    color: #64748b;
    font-weight: 500;
}

.file-date {
    color: #94a3b8;
    font-weight: 500;
}

.file-description {
    font-size: 0.875rem;
    color: #6b7280;
    margin-bottom: 0.75rem;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-clamp: 2;
    overflow: hidden;
}

.file-consultation {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
    border-radius: 8px;
    font-size: 0.75rem;
    color: #059669;
    font-weight: 600;
    margin-bottom: 0.75rem;
}

.file-consultation i {
    font-size: 0.875rem;
}

.file-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
}

.btn-action {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    padding: 0.625rem 0.75rem;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.875rem;
    font-weight: 600;
    color: #4b5563;
}

.btn-action:hover {
    background: #e5e7eb;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn-action i {
    font-size: 0.875rem;
}

.btn-action .btn-text {
    display: none;
}

.btn-action.btn-download:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border-color: transparent;
}

.btn-action.btn-view:hover {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    color: white;
    border-color: transparent;
}

.btn-action.btn-delete:hover {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    border-color: transparent;
}

/* Vista de lista */
.files-container.list .file-card {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.5rem;
}

.files-container.list .file-preview {
    font-size: 2rem;
    margin-bottom: 0;
    width: 60px;
    flex-shrink: 0;
}

.files-container.list .file-name {
    margin-bottom: 0.25rem;
}

.files-container.list .btn-action .btn-text {
    display: inline;
}

.files-container.list .file-actions {
    margin-top: 0;
    margin-left: auto;
    flex-shrink: 0;
}

.file-viewer {
    padding: 1.5rem;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.file-viewer.loading-viewer {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    padding: 2rem 1.5rem;
}

.loading-content {
    text-align: center;
    color: #667eea;
}

.loading-content i {
    color: #667eea;
    margin-bottom: 1rem;
}

.loading-content p {
    color: #6b7280;
    font-size: 1rem;
    margin: 0;
}

.viewer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    gap: 0;
    flex-wrap: nowrap;
    padding-right: 4.5rem;
}

.viewer-header h3 {
    margin: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 1.25rem;
    margin-right: auto;
}

.viewer-header .btn-copy-url,
.viewer-header .btn-primary {
    margin-left: 0.25rem;
}

.header-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%);
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 8px;
    color: #059669;
    font-size: 0.875rem;
    font-weight: 600;
}

.header-badge i {
    color: #10b981;
}

.btn-copy-url {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.btn-copy-url:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.btn-copy-url:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
}

.btn-copy-url i {
    font-size: 1rem;
}

.btn-primary {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.2);
}

.btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.btn-primary:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

.btn-primary i {
    font-size: 1rem;
}

.modal-close {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(15, 23, 42, 0.1);
    border-radius: 50%;
    color: #64748b;
    font-size: 1.25rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 10;
    backdrop-filter: blur(8px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.modal-close:hover {
    background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
    color: white;
    border-color: transparent;
    transform: rotate(90deg) scale(1.1);
    box-shadow: 0 4px 16px rgba(220, 38, 38, 0.35);
}

.modal-close:active {
    transform: rotate(90deg) scale(0.95);
}

.modal-close i {
    transition: transform 0.2s ease;
}

.viewer-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.viewer-content iframe {
    width: 100%;
    flex: 1;
    border: none;
    border-radius: 8px;
}

.empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3rem 2rem;
    min-height: 500px;
}

.empty-state-card {
    max-width: 600px;
    width: 100%;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 16px;
    padding: 3rem 2.5rem;
    text-align: center;
    box-shadow: 0 4px 20px rgba(15, 23, 42, 0.08);
    transition: all 0.3s ease;
}

.empty-state-card:hover {
    box-shadow: 0 8px 30px rgba(15, 23, 42, 0.12);
    transform: translateY(-4px);
}

.empty-icon-container {
    width: 120px;
    height: 120px;
    margin: 0 auto 2rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
    animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {

    0%,
    100% {
        transform: scale(1);
        box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
    }

    50% {
        transform: scale(1.05);
        box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4);
    }
}

.empty-icon-container i {
    font-size: 3.5rem;
    color: white;
}

.empty-state-card h3 {
    color: #1e293b;
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0 0 0.75rem;
    letter-spacing: -0.02em;
}

.empty-state-card p {
    color: #64748b;
    font-size: 1rem;
    line-height: 1.6;
    margin: 0 0 2rem;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
}

.upload-button {
    margin-bottom: 2rem;
    min-width: 200px;
    font-size: 1rem;
    padding: 0.875rem 2rem !important;
    box-shadow: 0 4px 14px rgba(102, 126, 234, 0.4) !important;
}

.upload-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5) !important;
}

.empty-features {
    display: flex;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.feature-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #64748b;
    font-size: 0.875rem;
}

.feature-item i {
    color: #10b981;
    font-size: 1rem;
}

.loading-container {
    text-align: center;
    padding: 3rem 2rem;
}

.spinner {
    border: 4px solid #f3f4f6;
    border-top: 4px solid #667eea;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.pagination-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
    background: #f8f9fa;
}

.btn-page {
    background: white;
    border: 1px solid rgba(15, 23, 42, 0.1);
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #475569;
    font-weight: 500;
}

.btn-page:hover:not(:disabled) {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: transparent;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.btn-page:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    font-weight: 500;
    color: #6b7280;
}

/* Modal styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 0;
}

.modal-content {
    background: white;
    border-radius: 12px;
    width: 90%;
    max-width: 1200px;
    max-height: 95vh;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    position: relative;
    display: flex;
    flex-direction: column;
}

.modal-large {
    width: 95%;
    max-width: 1400px;
    height: 95vh;
}
</style>
